"use client";

import { type RefObject, useEffect, useState } from "react";
import { trackJobImpressions } from "@/lib/analytics/track";

type Props = {
	/** Feed ranking variant that served the rows — attributed per impression so
	 * click-through can be measured against the algorithm that produced the
	 * order. */
	algoVersion: string;
	/** 1-based feed page being viewed. */
	page: number;
	locale?: string;
	surface?: string;
};

/** Fraction of a row that must be visible to count as an impression. */
export const VISIBILITY_THRESHOLD = 0.5;
/** Dwell before a visible row is counted — filters fast scroll-bys. */
export const DWELL_MS = 1000;
/** How often the queued batch is flushed to the beacon. */
const FLUSH_INTERVAL_MS = 4000;

/**
 * Captures one per-job `view` impression per feed render. A row counts once it
 * has been ≥50% visible for ≥1s (so a fast scroll-by is not an impression),
 * after which it is unobserved. Impressions are queued and flushed in batches
 * (one beacon, many rows) on an interval and on page-hide — this is the
 * "seen-but-skipped" signal the personalization ranker needs and that no
 * existing event captured.
 *
 * Reads `[data-job-id]` / `[data-job-position]` off the rendered rows
 * (`JobRow`), so it stays decoupled from the server-rendered feed markup.
 */
export function FeedImpressionTracker({
	algoVersion,
	page,
	locale,
	surface,
}: Props) {
	useEffect(() => {
		if (
			typeof IntersectionObserver === "undefined" ||
			typeof document === "undefined"
		) {
			return;
		}

		const rows = document.querySelectorAll<HTMLElement>("[data-job-id]");
		if (rows.length === 0) return;

		const queued = new Set<string>();
		const dwellTimers = new Map<Element, number>();
		let pending: Array<{ jobId: string; position?: number }> = [];

		const flush = () => {
			if (pending.length === 0) return;
			const items = pending;
			pending = [];
			trackJobImpressions({ items, surface, locale, algoVersion, page });
		};

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					const el = entry.target as HTMLElement;
					const jobId = el.dataset.jobId;
					if (!jobId) continue;

					if (
						entry.isIntersecting &&
						entry.intersectionRatio >= VISIBILITY_THRESHOLD
					) {
						if (dwellTimers.has(el) || queued.has(jobId)) continue;
						const timer = window.setTimeout(() => {
							dwellTimers.delete(el);
							if (queued.has(jobId)) return;
							queued.add(jobId);
							const posRaw = el.dataset.jobPosition;
							const position = posRaw ? Number.parseInt(posRaw, 10) : undefined;
							pending.push({
								jobId,
								position:
									position && Number.isFinite(position) ? position : undefined,
							});
							observer.unobserve(el);
						}, DWELL_MS);
						dwellTimers.set(el, timer);
					} else {
						// Scrolled away before the dwell elapsed — cancel.
						const timer = dwellTimers.get(el);
						if (timer !== undefined) {
							window.clearTimeout(timer);
							dwellTimers.delete(el);
						}
					}
				}
			},
			{ threshold: [VISIBILITY_THRESHOLD] },
		);

		for (const row of rows) observer.observe(row);

		const flushInterval = window.setInterval(flush, FLUSH_INTERVAL_MS);
		const onHide = () => {
			if (document.hidden) flush();
		};
		document.addEventListener("visibilitychange", onHide);
		window.addEventListener("pagehide", flush);

		return () => {
			observer.disconnect();
			for (const timer of dwellTimers.values()) window.clearTimeout(timer);
			dwellTimers.clear();
			window.clearInterval(flushInterval);
			document.removeEventListener("visibilitychange", onHide);
			window.removeEventListener("pagehide", flush);
			flush();
		};
	}, [algoVersion, page, locale, surface]);

	return null;
}

/**
 * Per-element twin of {@link FeedImpressionTracker}'s visibility rule: returns
 * true once `ref`'s element has been ≥50% visible for ≥1s, and stays true.
 *
 * `JobRow` gates its `feed_card_impression` on this. That event used to fire on
 * mount, so every row of a 20-row page counted as "seen" the moment it
 * rendered, below the fold or not (PostHog audit 2026-10-21) — which inflated
 * the impression denominator for every lower slot and made CTR-by-position
 * meaningless.
 *
 * Fails open (true at once) where `IntersectionObserver` does not exist, so an
 * old browser or a test DOM keeps the previous on-mount behaviour rather than
 * reporting nothing.
 */
export function useViewportImpression(ref: RefObject<Element | null>): boolean {
	const [seen, setSeen] = useState(false);
	useEffect(() => {
		if (seen) return;
		const el = ref.current;
		if (typeof IntersectionObserver === "undefined" || !el) {
			setSeen(true);
			return;
		}
		let timer: number | undefined;
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (
						entry.isIntersecting &&
						entry.intersectionRatio >= VISIBILITY_THRESHOLD
					) {
						if (timer !== undefined) continue;
						timer = window.setTimeout(() => {
							observer.disconnect();
							setSeen(true);
						}, DWELL_MS);
					} else if (timer !== undefined) {
						// Scrolled away before the dwell elapsed — cancel.
						window.clearTimeout(timer);
						timer = undefined;
					}
				}
			},
			{ threshold: [VISIBILITY_THRESHOLD] },
		);
		observer.observe(el);
		return () => {
			observer.disconnect();
			if (timer !== undefined) window.clearTimeout(timer);
		};
	}, [ref, seen]);
	return seen;
}
